import { encode } from "querystring";
import { parsePaginationHeaders } from "./parseHeaders";
import { ActiveUser } from "@/services/user";
import { FILES_ENDPOINTS } from "@/services/files";
import { BASE_URL } from "../constants/config";
import store from "../store";
import { translationsObject } from "../constants/translations";

const translate = (text_to_translate) => {
  const lang = store?.state?.language || "en";
  if (lang === "en") return text_to_translate;
  return translationsObject[text_to_translate]?.fr || text_to_translate;
};

export const redirectIfNotAuthenticated = (res) => {
  if (res.status === 401) {
    ActiveUser.clear();
    window.localStorage.setItem(
      "pathToLoadAfterLogin",
      window.location.pathname
    );
    return this.$router.replace("/account/login");
  }
  if (res.status === 403) return this.$router.replace("/");
};

export const request = async ({
  url,
  query,
  headers = {},
  method = "GET",
  body,
  formData,
  skipRedirect = false,
  ...rest
}) => {
  const methodLower = method.toLowerCase();
  const jsonMethods = ["post", "put", "patch", "delete"];
  if (body && jsonMethods.includes(methodLower) && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  if (body && typeof body === "object") {
    body = JSON.stringify(body);
  }
  if (formData) {
    body = formData;
  }
  const token = ActiveUser.getToken();
  if (token) {
    headers.Authorization = ActiveUser.getAuthorization();
  }
  if (query) {
    url = `${url}?${encode(query)}`;
  }
  const res = await fetch(url, {
    method,
    headers,
    body,
    ...rest,
  });

  if (!skipRedirect) {
    redirectIfNotAuthenticated(res);
  }

  const contentType = res.headers.get("content-type");
  if (contentType !== "application/json") {
    return {
      res,
      data: {},
      pagination: {},
      headers: res.headers,
    };
  }

  const data = await res.json();
  const pagination = parsePaginationHeaders(res.headers);

  return {
    res,
    data,
    pagination,
    headers: res.headers,
  };
};

export const getFileNameFromHeaders = (headers) => {
  return headers
    .get("content-disposition")
    ?.split("filename=")
    .pop()
    .replaceAll('"', "");
};

export const downloadFile = async ({
  name,
  fileId,
  downloadFileName = "",
  url = FILES_ENDPOINTS.download,
  headers = {},
  method = "GET",
  query = {},
  response = null,
}) => {
  query.name = name;
  query.file_id = fileId;
  url = `${url}?${encode(query)}`;
  if (BASE_URL !== "http://localhost:5000/") {
    url = url.replace("http://", "https://");
  }

  headers.Authorization = ActiveUser.getAuthorization();
  headers["Content-Type"] = "application/json";
  headers["Cache-Control"] = "no-cache";

  if (!response) {
    response = await fetch(url, {
      method,
      headers,
    });
  }
  if (response.status !== 200) {
    response.json().then((data) => {
      if (data && data.detail) return alert(data.detail);
      else return alert(translate("File not found"), {
                action: {
                    text: translate('Close'),
                    icon: 'close',
                    onClick: (e, toastObject) => {
                        toastObject.goAway(0);
                    }
                }
            });
    });
  }
  downloadFileName =
    downloadFileName || getFileNameFromHeaders(response.headers);
  const fileResponse = await response.blob();
  const objectUrl = window.URL.createObjectURL(fileResponse);
  const anchor = document.createElement("a");

  document.body.appendChild(anchor);
  anchor.href = objectUrl;
  anchor.download = downloadFileName;
  anchor.click();

  window.URL.revokeObjectURL(objectUrl);
  anchor.remove();
};

export const uploadFile = ({
  fileId,
  file,
  onProgress,
  onLoad,
  onStateChange,
  fileName,
  query = {},
  url = FILES_ENDPOINTS.upload,
  method = "POST",
  showAlerts = true,
}) => {
  return new Promise((resolve) => {
    const resolveUpload = (message, extraData, error = null) => {
      if (typeof message === "string" && showAlerts) {
        alert(translate(message));
      } else {
        console.warn("Upload error", message);
        if (message.extra_data && showAlerts) {
          alert(translate(message.extra_data));
        }
      }
      if (error) resolve({ error: error });
      resolve(extraData);
    };

    query.file_id = fileId;
    url = `${url}?${encode(query)}`;

    const formData = new FormData();
    fileName = fileName || file.name;
    formData.append("file", file, fileName);
    formData.append("filename", fileName);

    // if (file.size > 250000000) {
    //   return resolveUpload("File is too big");
    // }
    const xhr = new XMLHttpRequest();
    xhr.onreadystatechange = () => {
      onStateChange && onStateChange(xhr, file.name);
      if (xhr.readyState === XMLHttpRequest.DONE) {
        onProgress && onProgress(100);
        let response
        try {
          response = JSON.parse(xhr.responseText);
        } catch (e) {
          response = xhr.responseText;
        }
        if (xhr.status === 201 || xhr.status === 200) {
          return resolveUpload(
            translate("File was uploaded successfully."),
            response
          );
        }
        redirectIfNotAuthenticated(xhr);
        if (response?.detail) {
          return resolveUpload(
            response.detail,
            response.detail.extra_data,
            response.detail
          );
        }
        resolveUpload(translate("Error occurred during upload."));
      }
    };
    xhr.upload.onload = onLoad;

    xhr.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress((e.loaded / e.total) * 100);
      }
    };
    onProgress && onProgress(0);

    xhr.open(method, url);
    xhr.setRequestHeader("authorization", ActiveUser.getAuthorization());
    xhr.send(formData);
  });
};
